
<template>
  <div class="question-container" :class="[displayMode, { 'compact-tree': compactTree }]">
    <template v-if="isPresentationMode">
      <div
        v-for="group in displayQuestionGroups"
        :key="group.key"
        class="question-group"
        :class="{ 'has-visible-root': group.hasVisibleRoot, 'incomplete-group': !group.hasVisibleRoot }"
      >
        <div
          v-for="item in group.questions"
          :key="item.id"
          class="question-item"
          :class="{
            'tree-question-item': item._tree.isTreeQuestion,
            'tree-root-item': isGroupRoot(item, group),
            'tree-child-item': !isGroupRoot(item, group),
            'relation-incomplete': item._tree.relationIncomplete
          }"
        >
          <Question
            :class="item.hm_rate && item.hm_rate.length > 0 && 'has-rating'"
            :questionName="item.title"
            :questionType="item.type"
            :answer="formatValue(item)"
            :videoResourceKey="item.answer_file_key"
            :isHr="isHr"
            :hideAnswer="hideAnswer"
            :questionLabelI18nKey="questionLabelI18nKey"
            :answerLabelI18nKey="answerLabelI18nKey"
            :videoLabelI18nKey="videoLabelI18nKey"
            :noteLabelI18nKey="noteLabelI18nKey"
            :depth="item._tree.depth"
            :parentQuestionId="item._tree.parentQuestionId"
            :displayMode="displayMode"
            :questionNumber="isGroupRoot(item, group) ? group.number : null"
            :isRoot="isGroupRoot(item, group)"
            :required="Boolean(item.required)"
            :relationIncomplete="Boolean(item._tree.relationIncomplete)"
            @videoPlayIconClick="handleVideoPlayIconClick(item, $event)"
            @viewAttachmentBtnClick="handleViewAttachmentBtnClick(item, $event)"
          />
          <div v-if="item.hm_rate && item.hm_rate.length > 0" class="rating-container">
            <Rating
              v-for="(rate, index) in item.hm_rate"
              :key="index"
              :time="rate.invite_record.submit_time"
              :comment="rate.comment"
              :name="rate.invite_record.hire_manager.full_name"
              :rate="rate.rate"
            />
            <div class="total-score">{{ formatRateScore(item.hm_rate_avg) }}</div>
          </div>
        </div>
      </div>
    </template>
    <template v-else>
    <div
      v-for="(item, index) in displayQuestionnaireResultList"
      class="question-item"
      :class="{
        'tree-question-item': item._tree.isTreeQuestion,
        'tree-root-item': item._tree.isTreeRoot,
        'tree-child-item': item._tree.depth > 0
      }"
      :style="getTreeItemStyle(item)"
      :key="screeningResult ? item.id : index"
    >
      <Question
        :class="item.hm_rate && item.hm_rate.length > 0 && 'has-rating'"
        :questionName="item.title"
        :questionType="item.type"
        :answer="formatValue(item)"
        :videoResourceKey="item.answer_file_key"
        :isHr="isHr"
        :hideAnswer="hideAnswer"
        :questionLabelI18nKey="questionLabelI18nKey"
        :answerLabelI18nKey="answerLabelI18nKey"
        :videoLabelI18nKey="videoLabelI18nKey"
        :noteLabelI18nKey="noteLabelI18nKey"
        :depth="item._tree.depth"
        :parentQuestionId="item._tree.parentQuestionId"
        @videoPlayIconClick="handleVideoPlayIconClick(item, $event)"
        @viewAttachmentBtnClick="handleViewAttachmentBtnClick(item, $event)"
      >
      </Question>
      <div v-if="item.hm_rate && item.hm_rate.length > 0" class="rating-container">
        <Rating
          v-for="(rate, index) in item.hm_rate"
          :key="index"
          :time="rate.invite_record.submit_time"
          :comment="rate.comment"
          :name="rate.invite_record.hire_manager.full_name"
          :rate="rate.rate"
        ></Rating>
        <div class="total-score">{{ formatRateScore(item.hm_rate_avg) }}</div>
      </div>
    </div>
    </template>
  </div>
</template>

<script>
import _ from 'lodash'
import Question from './question.vue'
import Rating from './rating.vue'
import { buildScreeningQuestionDisplay } from '@/utils/openQuestionTree'

const SELECT_TYPE = ['multiple_select', 'single_select', 'dropdown', 'checkbox']

export default {
  components: {
    Question,
    Rating
  },
  props: {
    screeningResult: {
      type: Boolean,
      default: false
    },
    questionnaireResultList: {
      type: Array,
      required: true
    },
    isHr: {
      type: Boolean,
      default: false
    },
    hideAnswer: {
      type: Boolean,
      default: false
    },
    compactTree: {
      type: Boolean,
      default: false
    },
    displayMode: {
      type: String,
      default: ''
    },
    questionLabelI18nKey: {
      default: 'admin.application.candidate.label.questionnaireQuestion'
    },
    answerLabelI18nKey: {
      default: 'admin.application.candidate.label.answer'
    },
    videoLabelI18nKey: {
      default: 'admin.application.candidate.label.video'
    },
    noteLabelI18nKey: {
      default: 'admin.application.candidate.label.note'
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate',
      expire: null
    }
  },
  computed: {
    isPresentationMode () {
      return ['screening-result', 'hr-sidebar'].includes(this.displayMode)
    },
    displayQuestionnaireResultList () {
      if (this.screeningResult) return buildScreeningQuestionDisplay(this.questionnaireResultList)
      if (!this.isTreeQuestionnaire) {
        return _.map(this.questionnaireResultList, item => this.buildDisplayQuestion(item))
      }
      const questionMap = _.keyBy(this.questionnaireResultList, 'id')
      return _.map(this.questionnaireResultList, item => {
        const displayQuestion = this.buildDisplayQuestion(item)
        const parent = this.getParentQuestion(item, questionMap)

        if (parent) {
          displayQuestion._tree.parent = {
            id: parent.id,
            title: parent.title,
            depth: this.normalizeDepth(parent.depth)
          }
          displayQuestion._tree.parentQuestionId = parent.id || null
          displayQuestion._tree.parentAnswer = this.formatValue(parent)
        }
        displayQuestion._tree.isTreeRoot = displayQuestion._tree.depth === 0
        displayQuestion._tree.answerPath = this.buildDisplayAnswerPath(displayQuestion, parent)
        return displayQuestion
      })
    },
    displayQuestionGroups () {
      if (!this.isPresentationMode) return []
      const rows = this.displayQuestionnaireResultList
      const rowMap = new Map(rows.map(row => [String(row.id), row]))
      const groups = []
      const groupMap = new Map()

      if (this.displayMode !== 'screening-result') {
        rows.forEach(row => {
          const rootQuestionId = this.findVisibleRootQuestionId(row, rowMap)
          const rootKey = rootQuestionId === null || rootQuestionId === undefined
            ? null
            : String(rootQuestionId)
          const hasVisibleRoot = Boolean(rootKey && rowMap.has(rootKey))
          const groupKey = hasVisibleRoot ? `root:${rootKey}` : `incomplete:${row.id}`
          let group = groupMap.get(groupKey)
          if (!group) {
            group = {
              key: groupKey,
              rootQuestionId: hasVisibleRoot ? rootKey : null,
              hasVisibleRoot,
              questions: []
            }
            groupMap.set(groupKey, group)
            groups.push(group)
          }
          group.questions.push(row)
        })
      } else {
        // Root cards follow their own input order. Descendants are appended in
        // their input order afterwards, so a shuffled child never precedes root.
        rows.forEach((row, index) => {
          const rootKey = row._tree.rootQuestionId === null || row._tree.rootQuestionId === undefined
            ? null
            : String(row._tree.rootQuestionId)
          if (!rootKey || !rowMap.has(rootKey) || String(row.id) !== rootKey || groupMap.has(`root:${rootKey}`)) return
          const group = { key: `root:${rootKey}`, rootQuestionId: rootKey, hasVisibleRoot: true, questions: [row], anchor: index }
          groupMap.set(group.key, group)
          groups.push(group)
        })

        rows.forEach((row, index) => {
          const rootKey = row._tree.rootQuestionId === null || row._tree.rootQuestionId === undefined
            ? null
            : String(row._tree.rootQuestionId)
          const group = rootKey && groupMap.get(`root:${rootKey}`)
          if (group) {
            if (String(row.id) !== rootKey) group.questions.push(row)
            return
          }
          const orphanGroup = { key: `incomplete:${row.id}`, rootQuestionId: null, hasVisibleRoot: false, questions: [row], anchor: index }
          groups.push(orphanGroup)
        })
      }

      let rootNumber = 0
      const orderedGroups = this.displayMode === 'screening-result'
        ? groups.sort((left, right) => left.anchor - right.anchor)
        : groups
      return orderedGroups.map(({ anchor, ...group }) => {
        const numberedGroup = {
          ...group,
          number: group.hasVisibleRoot ? ++rootNumber : null
        }
        return {
          ...numberedGroup,
          questions: group.questions.map(question => ({
            ...question,
            _screeningRelationContext: this.buildScreeningRelationContext(question, numberedGroup)
          }))
        }
      })
    },
    isTreeQuestionnaire () {
      return _.some(this.questionnaireResultList, item => {
        return (
          this.normalizeDepth(item.depth) > 0 ||
          Boolean(item.tree_path) ||
          Boolean(item.parent_question_id || item.answer_parent_question_id) ||
          _.some(item.options || item.option, option => Boolean(option.next_question_id)) ||
          !_.isEmpty(item.answer_path || item.answer_answer_path)
        )
      })
    }
  },
  created () {

  },
  methods: {
    async handleVideoPlayIconClick (question, $event) {
      if (!question?.title || !question.answer_file_key) {
        return
      }
      this.$emit('videoPlayIconClick', question, $event)
    },
    async handleViewAttachmentBtnClick (question, $event) {
      if (!question.answer_file_key) {
        return
      }
      this.$emit('viewAttachmentBtnClick', question, $event)
    },
    formatValue (question) {
      if (SELECT_TYPE.includes(question.type)) {
        return _.map(question.answer_option, 'description').join(', ')
      }
      return question.answer_value
    },
    normalizeDepth (depth) {
      const parsedDepth = Number(depth)
      return Number.isNaN(parsedDepth) ? 0 : Math.max(parsedDepth, 0)
    },
    findVisibleRootQuestionId (question, rowMap) {
      if (!question._tree.isTreeQuestion || question._tree.depth === 0) return String(question.id)
      const visited = new Set([String(question.id)])
      let parentId = question._tree.parentQuestionId
      while (parentId !== null && parentId !== undefined && parentId !== '') {
        const key = String(parentId)
        if (visited.has(key) || !rowMap.has(key)) return null
        visited.add(key)
        const parent = rowMap.get(key)
        if (parent._tree.depth === 0) return key
        parentId = parent._tree.parentQuestionId
      }
      return null
    },
    isGroupRoot (question, group) {
      return group.hasVisibleRoot && String(question.id) === group.rootQuestionId
    },
    buildScreeningRelationContext (question, group) {
      if (this.displayMode !== 'screening-result') return null
      if (group.hasVisibleRoot) return null

      const parent = question._tree.parent
      return {
        showIncomplete: true,
        parentTitle: parent && parent.title ? parent.title : null
      }
    },
    buildDisplayQuestion (question) {
      const answerPath = this.formatAnswerPath(question.answer_path || question.answer_answer_path)
      const depth = this.normalizeDepth(question.depth || _.last(answerPath)?.depth)
      return {
        ...question,
        _tree: {
          depth,
          isTreeQuestion: this.isTreeQuestionnaire,
          isTreeRoot: this.isTreeQuestionnaire && depth === 0,
          parent: null,
          parentQuestionId: question.parent_question_id || question.answer_parent_question_id || null,
          parentAnswer: null,
          answerPath
        }
      }
    },
    getParentQuestion (question, questionMap) {
      const parentId = question.parent_question_id || question.answer_parent_question_id
      if (parentId && questionMap[parentId]) {
        return questionMap[parentId]
      }
      const configuredParent = _.find(this.questionnaireResultList, candidateParent => {
        return _.some(candidateParent.options || candidateParent.option, option => {
          return Number(option.next_question_id) === Number(question.id)
        })
      })
      if (configuredParent) {
        return configuredParent
      }
      const treePathQuestionIds = this.getTreePathQuestionIds(question.tree_path)
      if (treePathQuestionIds.length > 1) {
        const configuredParentId = treePathQuestionIds[treePathQuestionIds.length - 2]
        if (questionMap[configuredParentId]) {
          return questionMap[configuredParentId]
        }
      }
      return null
    },
    getTreePathQuestionIds (treePath) {
      const questionIds = []
      const questionPattern = /\/question\/(\d+)/g
      let match = questionPattern.exec(treePath || '')
      while (match) {
        questionIds.push(Number(match[1]))
        match = questionPattern.exec(treePath)
      }
      return questionIds
    },
    buildDisplayAnswerPath (question, parent) {
      if (!_.isEmpty(question._tree.answerPath)) {
        return question._tree.answerPath
      }
      const parentPath = this.formatAnswerPath(parent && (parent.answer_path || parent.answer_answer_path))
      if (_.isEmpty(parentPath)) {
        return []
      }
      return [
        ...parentPath,
        {
          depth: question._tree.depth,
          title: question.title,
          optionLabel: this.formatValue(question)
        }
      ]
    },
    getTreeItemStyle (question) {
      if (!question._tree.isTreeQuestion || this.compactTree) {
        return null
      }
      return {
        '--tree-indent': `${Math.min(question._tree.depth * 34, 204)}px`
      }
    },
    formatRateScore (score) {
      return _.round(score, 1)
    },
    // 格式化答题路径
    formatAnswerPath (answerPath) {
      if (!answerPath || !_.isArray(answerPath)) {
        return []
      }
      return _.map(answerPath, (node, index) => {
        return {
          depth: node.depth || index,
          title: node.title || '',
          optionLabel: node.option_label || node.optionLabel || ''
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.question-container {
  &.compact-tree {
    .question-item.tree-question-item {
      padding-left: 0;

      &::before,
      &::after {
        display: none;
      }
    }

    .question-item.tree-question-item .tree-root-marker,
    .question-item.tree-question-item .tree-relation {
      box-sizing: border-box;
      max-width: 100%;
      min-width: 0;

      .tree-level-tag,
      .relation-label {
        flex-shrink: 0;
      }

      .parent-title {
        min-width: 0;
        max-width: none;
        overflow-wrap: anywhere;
        white-space: normal;
        word-break: break-word;
      }
    }

    ::v-deep .question-item > .question-container {
      min-width: 0;

      .question,
      .answer,
      .remarks {
        min-width: 0;

        .content {
          min-width: 0;
          overflow-wrap: anywhere;
          word-break: break-word;

          .depth-tag {
            flex-shrink: 0;
          }
        }
      }
    }
  }

  .question-item {
    position: relative;
    margin-top: 50px;

    &.tree-question-item {
      margin-top: 24px;
      padding-left: var(--tree-indent);

      &::before {
        content: '';
        position: absolute;
        top: 0;
        bottom: -24px;
        left: calc(var(--tree-indent) - 18px);
        width: 2px;
        background: #d8ecf5;
      }

      &.tree-root-item::before {
        display: none;
      }

      &.tree-child-item::after {
        content: '';
        position: absolute;
        top: 18px;
        left: calc(var(--tree-indent) - 18px);
        width: 18px;
        height: 2px;
        background: #0096cc;
      }
    }

    .tree-root-marker,
    .tree-relation {
      display: inline-flex;
      align-items: center;
      max-width: 100%;
      margin-bottom: 8px;
      padding: 6px 10px;
      border-radius: 14px;
      color: #606266;
      background: #eef8fc;
      font-size: 12px;
      line-height: 16px;
      word-break: break-word;
    }

    .tree-relation {
      position: relative;
      border: 1px solid #c7e8f4;
      background: #fff;

      .relation-label {
        color: #909399;
        margin-right: 4px;
      }

      .parent-title {
        max-width: 240px;
        color: #303133;
        font-weight: 500;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .parent-answer {
        margin-left: 8px;
        padding-left: 8px;
        border-left: 1px solid #dcdfe6;
        color: #0096cc;
      }
    }

    .tree-level-tag {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 28px;
      height: 20px;
      margin-right: 8px;
      padding: 0 6px;
      border-radius: 10px;
      color: #fff;
      background: #0096cc;
      font-weight: bold;
      line-height: 20px;
    }

    .tree-answer-path {
      margin-bottom: 15px;
      padding: 12px 15px;
      background: #f5f7fa;
      border-radius: 4px;
      border-left: 3px solid #0096cc;

      .path-label {
        color: #606266;
        font-size: 14px;
        font-weight: 500;
        margin-bottom: 8px;
      }

      .path-content {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px;

        .path-node {
          display: flex;
          align-items: center;
          gap: 4px;

          .depth-tag {
            background: #0096cc;
            color: #fff;
            padding: 2px 6px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: bold;
          }

          .node-title {
            color: #303133;
            font-size: 13px;
          }

          .node-answer {
            color: #67c23a;
            font-size: 12px;
          }

          .path-arrow {
            color: #909399;
            margin: 0 4px;
          }
        }
      }
    }

    .has-rating {
      background: #FBFBFB;
    }

    .rating-container {
      margin-top: -1px;
    }

    .total-score {
      position: absolute;
      right: 15px;
      top: 15px;
      width: 60px;
      height: 60px;
      line-height: 60px;
      text-align: center;
      font-size: 30px;
      font-weight: bold;
      border: 2px solid #0096cc;
      color: #0096cc;
      border-radius: 50%;
      background-color: #fff;
    }

    &:first-child {
      margin-top: 0;
    }

  }
}

.question-container.screening-result {
  .question-group {
    margin-top: 18px;
    overflow: hidden;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    background: #fff;

    &:first-child {
      margin-top: 0;
    }

    &.incomplete-group {
      border-style: dashed;
    }
  }

  .question-item {
    margin-top: 0;

    &.tree-question-item {
      padding-left: 0;

      &::before,
      &::after {
        display: none;
      }
    }

    &.tree-child-item {
      margin: 0 20px 18px;
      overflow: hidden;
      border: 1px solid #dcdfe6;
      border-radius: 4px;
    }
  }

  .incomplete-relation {
    display: flex;
    gap: 8px;
    padding: 8px 20px;
    color: #909399;
    background: #fafafa;
    font-size: 12px;
    line-height: 18px;

    .parent-title {
      color: #606266;
      overflow-wrap: anywhere;
    }
  }

  .rating-container {
    margin-top: 0;
  }
}

.question-container.hr-sidebar {
  .question-group {
    display: flex;
    flex-direction: column;
    margin-top: 16px;
    padding-bottom: 11px;
    border-bottom: 1px solid #dcdfe6;
    background: #f7f8fa;

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      border-bottom: 0;
    }
  }

  .question-item {
    margin-top: 0;

    &.tree-question-item {
      padding-left: 0;

      &::before,
      &::after {
        display: none;
      }
    }

    &.tree-root-item {
      + .tree-child-item {
        margin-top: 8px;
        padding-top: 10px;
        border-radius: 4px 4px 0 0;
      }
    }

    &.tree-child-item {
      position: relative;
      margin: 0;
      padding: 12px 10px;
      background: #fff;

      &:not(:last-child)::after {
        content: '';
        display: block;
        position: absolute;
        top: auto;
        right: 10px;
        bottom: 0;
        left: 10px;
        width: auto;
        height: 0;
        border-bottom: 1px solid #dcdfe6;
        background: transparent;
      }

      &:last-child {
        padding-bottom: 10px;
        border-radius: 0 0 4px 4px;
      }
    }

    &.tree-root-item + .tree-child-item:last-child {
      border-radius: 4px;
    }
  }
}
</style>
